// components/Footer.tsx - FennoFuture Footer
import React from 'react';
import { Link } from 'react-router-dom';
import {
  Mail, Phone, MapPin, Building, FileText, Award,
  Heart, ArrowUp
} from 'lucide-react';

const Footer: React.FC = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navItems = [
    { href: '/palvelut', label: 'Palvelut' },
    { href: '/yhteystiedot', label: 'Yhteystiedot' },
    { href: '/privacy', label: 'Tietosuoja' },
    { href: '/terms', label: 'Käyttöehdot' },
  ];

  return (
    <footer className="relative bg-gradient-to-b from-gray-900 to-black text-white">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500 to-transparent" />

      <div className="relative pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12 items-start">

            <div>
              <Link to="/" className="flex items-center space-x-3 mb-6 group">
                <div className="relative w-12 h-12 transform transition-transform group-hover:scale-110">
                  <img src="/logo.png" alt="FennoFuture" className="w-full h-full object-contain" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">FennoFuture</h3>
                  <p className="text-sm text-gray-400">Suunnittelu- ja valmistuspalvelut</p>
                </div>
              </Link>

              <p className="text-gray-300 mb-6 leading-relaxed">
                Tarjoamme laadukkaita tuotteiden suunnittelu- ja valmistuspalveluita,
                jotka perustuvat pitkäaikaiseen tekniseen kokemukseen.
              </p>

              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Building className="w-4 h-4" />
                  <span>FennoFuture</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <FileText className="w-4 h-4" />
                  <span>Y-tunnus: 3203489-7</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Award className="w-4 h-4" />
                  <span>Perustettu: 2021</span>
                </div>
              </div>
            </div>

            <div className="lg:pt-[76px]">
              <h4 className="text-lg font-semibold mb-6 flex items-center gap-2">
                <div className="w-1 h-6 bg-gradient-to-b from-blue-500 to-purple-500 rounded-full" />
                Pikalinkit
              </h4>
              <ul className="space-y-3">
                {navItems.map((item, index) => (
                  <li key={index}>
                    <Link
                      to={item.href}
                      className="text-gray-300 hover:text-white transition-all duration-300 flex items-center gap-2 group"
                    >
                      <span className="w-0 group-hover:w-4 h-px bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-300" />
                      <span className="group-hover:translate-x-1 transition-transform duration-300">{item.label}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:pt-[76px]">
              <h4 className="text-lg font-semibold mb-6 flex items-center gap-2">
                <div className="w-1 h-6 bg-gradient-to-b from-green-500 to-emerald-500 rounded-full" />
                Yhteystiedot
              </h4>
              <ul className="space-y-4">
                <li>
                  <a href="mailto:innovation@fennofuture.fi" className="flex items-start gap-3 text-gray-300 hover:text-white transition-colors group">
                    <Mail className="w-5 h-5 mt-0.5 group-hover:scale-110 transition-transform" />
                    <div>
                      <div className="font-medium">innovation@fennofuture.fi</div>
                      <div className="text-xs text-gray-500">Sähköposti</div>
                    </div>
                  </a>
                </li>
                <li>
                  <a href="tel:+358400416466" className="flex items-start gap-3 text-gray-300 hover:text-white transition-colors group">
                    <Phone className="w-5 h-5 mt-0.5 group-hover:scale-110 transition-transform" />
                    <div>
                      <div className="font-medium">+358 400 416 466</div>
                      <div className="text-xs text-gray-500">Puhelin</div>
                    </div>
                  </a>
                </li>
                <li>
                  <div className="flex items-start gap-3 text-gray-300">
                    <MapPin className="w-5 h-5 mt-0.5" />
                    <div>
                      <div className="font-medium">Turuntie 3, 37830 Akaa</div>
                      <div className="text-xs text-gray-500">Akaa / Sastamala / Urjala</div>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-800">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <span>© 2026 FennoFuture.</span>
                <span>Kaikki oikeudet pidätetään.</span>
              </div>

              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <span>Tehty</span>
                <Heart className="w-4 h-4 text-red-500" />
                <span>Suomessa</span>
              </div>
            </div>
          </div>
        </div>

        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 group"
          aria-label="Scroll to top"
        >
          <ArrowUp className="w-5 h-5 transition-transform group-hover:-translate-y-1" />
        </button>
      </div>
    </footer>
  );
};

export default Footer;
